import { Suspense, lazy, type ComponentType, useEffect, useMemo } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { zoneManagementPath } from '../zones/zoneTypes';
import { Skeleton } from '@mui/material';
import { resolveServiceTabs } from '../../constants/moduleFlows';
import type { ServiceDetailContext } from './ServiceDetailLayout';

const ServiceSummaryTab = lazy(() => import('./tabs/ServiceSummaryTab'));
const ServiceBookingsTab = lazy(() => import('./tabs/ServiceBookingsTab'));
const ServiceManualBookingTab = lazy(() => import('./tabs/ServiceManualBookingTab'));
const ServiceReviewsTab = lazy(() => import('./tabs/ServiceReviewsTab'));
const ServiceEarningsTab = lazy(() => import('./tabs/ServiceEarningsTab'));
const ServiceSettingsTab = lazy(() => import('./tabs/ServiceSettingsTab'));
const ServiceBiddingTab = lazy(() => import('./tabs/ServiceBiddingTab'));
const ServicePricingTab = lazy(() => import('./tabs/ServicePricingTab'));
const ServiceUsersTab = lazy(() => import('./tabs/ServiceUsersTab'));
const ServicePartnersTab = lazy(() => import('./tabs/ServicePartnersTab'));
const ServiceCatalogTab = lazy(() => import('./tabs/ServiceCatalogTab'));
const ServiceMastersTab = lazy(() => import('./tabs/ServiceMastersTab'));
const ServiceZonePricingTab = lazy(() => import('./tabs/ServiceZonePricingTab'));
const TransportPartnerPayoutTab = lazy(() => import('./tabs/TransportPartnerPayoutTab'));
const ServiceOrderHistoryTab = lazy(() => import('./tabs/ServiceOrderHistoryTab'));
const ServiceScheduledOrdersTab = lazy(() => import('./tabs/ServiceScheduledOrdersTab'));
const ServiceRentalMasterTab = lazy(() => import('./tabs/ServiceRentalMasterTab'));
const ServiceOutstationMasterTab = lazy(() => import('./tabs/ServiceOutstationMasterTab'));
const ServiceTrackingTab = lazy(() => import('./tabs/ServiceTrackingTab'));
const ServiceCorporateAccountsTab = lazy(() => import('./tabs/ServiceCorporateAccountsTab'));
const ServiceQuotationsTab = lazy(() => import('./tabs/ServiceQuotationsTab'));
const PorterVehiclesTab = lazy(() => import('./tabs/PorterVehiclesTab'));
const PorterCatalogTab = lazy(() => import('./tabs/PorterCatalogTab'));
const PorterPackagesTab = lazy(() => import('./tabs/PorterPackagesTab'));
const PorterCorporateTab = lazy(() => import('./tabs/PorterCorporateTab'));
const PorterSummaryTab = lazy(() => import('./tabs/PorterSummaryTab'));

const TAB_COMPONENTS: Record<string, ComponentType> = {
  summary: ServiceSummaryTab,
  bookings: ServiceBookingsTab,
  'order-history': ServiceOrderHistoryTab,
  'scheduled-orders': ServiceScheduledOrdersTab,
  'manual-booking': ServiceManualBookingTab,
  quotations: ServiceQuotationsTab,
  reviews: ServiceReviewsTab,
  earnings: ServiceEarningsTab,
  settings: ServiceSettingsTab,
  pricing: ServicePricingTab,
  bidding: ServiceBiddingTab,
  corporate: ServiceCorporateAccountsTab,
  users: ServiceUsersTab,
  partners: ServicePartnersTab,
  'partner-payout': TransportPartnerPayoutTab,
  catalog: ServiceCatalogTab,
  masters: ServiceMastersTab,
  zones: ServiceZonePricingTab,
  fleet: ServiceRentalMasterTab,
  routes: ServiceOutstationMasterTab,
  tracking: ServiceTrackingTab,
};

function isPorter(service?: { slug?: string; settings?: Record<string, unknown>; moduleKey?: string }) {
  return service?.slug === 'porter-service'
    || service?.settings?.platformModule === 'porter'
    || service?.moduleKey === 'porter';
}

function TabFallback() {
  return <Skeleton variant="rounded" height={280} sx={{ borderRadius: 2 }} />;
}

export default function ServiceFlowTabRouter() {
  const { tab, hubType, slug } = useParams<{ tab?: string; hubType: string; slug: string }>();
  const { service, hub } = useOutletContext<ServiceDetailContext>();
  const navigate = useNavigate();
  const activeTab = tab || 'summary';
  const porter = isPorter(service);
  const allowedTabs = useMemo(
    () => new Set(resolveServiceTabs(hub, slug, service?.moduleKey).map((t) => t.key)),
    [hub, slug, service?.moduleKey],
  );

  useEffect(() => {
    if (!hubType || !slug || allowedTabs.has(activeTab as never)) return;
    if (activeTab === 'zones' || activeTab === 'pricing' || activeTab === 'partner-payout') {
      navigate(zoneManagementPath({ hub: hubType, service: slug }), { replace: true });
      return;
    }
    navigate(`/hubs/${hubType}/${slug}/summary`, { replace: true });
  }, [activeTab, allowedTabs, hubType, slug, navigate]);

  if (!allowedTabs.has(activeTab as never)) {
    return null;
  }

  let Component = TAB_COMPONENTS[activeTab] || ServiceSummaryTab;
  if (porter) {
    if (activeTab === 'summary') Component = PorterSummaryTab;
    if (activeTab === 'fleet') Component = PorterVehiclesTab;
    if (activeTab === 'catalog') Component = PorterCatalogTab;
    if (activeTab === 'packages') Component = PorterPackagesTab;
    if (activeTab === 'corporate') Component = PorterCorporateTab;
  }

  return (
    <Suspense fallback={<TabFallback />}>
      <Component key={`${hub}-${service?.slug}-${activeTab}`} />
    </Suspense>
  );
}
